import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  CalendarRange,
  Inbox,
  type LucideIcon,
} from "lucide-react";
import { api } from "../../lib/api";

interface Stats {
  companyCount: number;
  anfrageCount: number;
  thisMonth: number;
  lastMonth: number;
}

export function MasterStats() {
  const { data, isLoading } = useQuery({
    queryKey: ["master-stats"],
    queryFn: () => api<Stats>("/master/stats"),
  });

  if (isLoading || !data) return <p className="text-slate-500">Lädt…</p>;

  const delta = data.thisMonth - data.lastMonth;
  const pct =
    data.lastMonth > 0
      ? Math.round((delta / data.lastMonth) * 100)
      : data.thisMonth > 0
        ? 100
        : 0;

  return (
    <div>
      <h1 className="page-title mb-1">Statistik</h1>
      <p className="mb-5 text-sm text-slate-500">Globaler Überblick.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={Building2}
          label="Unternehmen"
          value={data.companyCount}
        />
        <Tile icon={Inbox} label="Anfragen gesamt" value={data.anfrageCount} />
        <Tile
          icon={CalendarClock}
          label="Letzte 30 Tage"
          value={data.thisMonth}
          delta={pct}
        />
        <Tile
          icon={CalendarRange}
          label="30–60 Tage zuvor"
          value={data.lastMonth}
        />
      </div>
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  delta,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  delta?: number;
}) {
  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
          <Icon className="h-4 w-4" />
        </div>
        {delta !== undefined && (
          <span
            className={`pill ${
              delta >= 0
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-red-50 text-red-700 ring-1 ring-red-200"
            }`}
          >
            {delta >= 0 ? "+" : ""}
            {delta}%
          </span>
        )}
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
        {value.toLocaleString("de-DE")}
      </div>
    </div>
  );
}
